import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useDispatch } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { auth } from "../../../firebase";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertValue, alertOpen } from "../../../features/alert.slice";
import pointDataQuery from "../../../firestore/PointData.query";
import { PointHistoryData, newPointHistory } from "../../../model/PointData";
import { Spiner } from "../../../components/spinner/Spiner";
import { convertDateTimeToJapaneseStringHalfwidth, convertDateToJapaneseStringWithWeekZero, convertTimeToString } from "../../../utility/ConvertUtily";
import { Messages, newMessage } from "../../../model/Message";
import messagesQuery from "../../../firestore/Messages.query";
import { ConstMessage } from "../../../config/const";

export const PointHistoryWebInfo = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ログイン中のユーザ情報
  const currentUser = auth.currentUser;
  // URLパラメータからpointHistoryId取得
  const { pointHistoryId } = useParams<{ pointHistoryId: string }>();
  const href = useRef<string>(window.location.href);

  const [isLoad, setIsLoad] = useState(true);
  const [pointHistory, setPointHistory] = useState<PointHistoryData>(newPointHistory);
  const [message, setMessage] = useState<Messages>(newMessage);

  const location = useLocation();

  useEffect(() => {
    const windowBreakPoint = 1024;
    if (href.current.includes('eventParticipationWeb')) {
      if (window.innerWidth < windowBreakPoint) {
        // 画面幅が1024px未満の場合
        navigate(`/portal/pointHistoryListMobile/${pointHistoryId}/info`);
      }
    } else {
      if (window.innerWidth >= windowBreakPoint) {
        // 画面幅が1024px以上の場合
        navigate(`/portal/pointHistoryListWeb/${pointHistoryId}/info`);
      }
    }
  },[location.pathname, pointHistoryId, navigate]);

  useMemo(() => {
    const f = async () => {
      try {
        if (pointHistoryId) {
          const pointHistory = await pointDataQuery.getPointHistoryData(currentUser?.tenantId!, pointHistoryId);
          setPointHistory(pointHistory);
          if (pointHistory.eventMsgId) {
            const message = await messagesQuery.getMessage(pointHistory.eventMsgId);
            setMessage(message);
          }
        } else {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_POINT_HISTORY, "danger")));
        }
        setIsLoad(false);
      } catch (err :any) {
        console.error(err);
        let errorMessage = err.error;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_CONNECT_NETWORK, "danger")));
        } else {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_POINT_HISTORY, "danger")));
        }
        setIsLoad(false);
      }
    }
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.tenantId, dispatch]);

  const getDestinatione = () => {
    if (href.current.includes('pointHistoryListWeb')) {
      return "/portal/pointHistoryListWeb";
    } else {
      return "/portal/pointHistoryListMobile";
    }
  };

  return (
    <main>
      <div className="flex flex-col overflow-y-auto mb-5">
        <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
          <button
            className="flex px-4 justify-center font-medium rounded-md shadow transition ease-in duration-200 rounded h-10 px-2 text-sm items-center text-orange-500 bg-white border-2 border-orange-500 hover:bg-orange-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:ring-offset-orange-100"
            onClick={() => navigate(getDestinatione())}
          >
            <ChevronLeftIcon className="-ml-2 h-5 w-5" aria-hidden="true" />
            <span>戻る</span>
          </button>
        </nav>
        {isLoad ?
          <Spiner />
        :
          <div className="max-w-5xl pl-10 pr-3 mb-5">
            <div className="flex flex-col">
              <h2 className="text-2xl font-bold text-gray-900">{pointHistory.ptName}</h2>
              <div className="pl-8 py-3">
                <div className="text-gray-500">{convertDateTimeToJapaneseStringHalfwidth(new Date(pointHistory.occurrenceDate))}</div>
                <span className="text-3xl font-bold"><span className="text-5xl text-orange-500">{pointHistory.tranType === '利用' ? Math.abs(pointHistory.grantPoints).toLocaleString() : pointHistory.grantPoints.toLocaleString()}</span> pt</span>
                {
                  pointHistory.tranType === '付与' || pointHistory.tranType === '調整' ? 
                    pointHistory.grantFlg ?
                      <div className="py-3">
                        <div className="flex justify-center items-center relative text-white text-sm border-green-500 bg-green-500 border-[1px] border-solid border-r-0 h-6 w-fit px-2 md:px-4">
                          <span className="absolute bg-green-500 border-green-500 border-t-[1px] border-r-[1px] right-[-0.5rem] h-[1rem] w-[1rem] transform rotate-45" />
                          {'付与完了'}
                        </div>
                      </div>
                    :
                      <div className="py-3">
                        <div className="flex justify-center items-center relative text-white text-sm border-orange-500 bg-orange-500 border-[1px] border-solid border-r-0 h-6 w-fit px-2 md:px-4">
                          <span className="absolute bg-orange-500 border-orange-500 border-t-[1px] border-r-[1px] right-[-0.5rem] h-[1rem] w-[1rem] transform rotate-45" />
                          {'付与処理中'}
                        </div>
                      </div>
                  :
                    pointHistory.tranType === '失効' ?
                      <div className="py-3">
                        <div className="flex justify-center items-center relative text-white text-sm border-gray-500 bg-gray-500 border-[1px] border-solid border-r-0 h-6 w-fit px-2 md:px-4">
                          <span className="absolute bg-gray-500 border-gray-500 border-t-[1px] border-r-[1px] right-[-0.5rem] h-[1rem] w-[1rem] transform rotate-45" />
                          {'失効'}
                        </div>
                      </div>
                    : 
                      <></>
                }
              </div>
              
              {(pointHistory.eventMsgId) ?
                <>
                  <div className="gap-1 pt-6">
                    <div className="text-lg font-bold">イベント詳細</div>
                    <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 mt-2 ml-12">
                      <span className="text-right">タイトル</span>
                      <span>{message.title}</span>

                      <span className="text-right">開催日時</span>
                      <div>
                        <p>{convertDateToJapaneseStringWithWeekZero(new Date(message.eventAt))}</p>
                        <p>{convertTimeToString(new Date(message.eventAt))}〜{convertTimeToString(new Date(message.eventEndAt))}</p>
                      </div>

                      <span className="text-right">本文</span>
                      <div>
                        <p className="whitespace-pre-line">{message.body}</p>
                      </div>
                    </div>
                  </div>
                </>
              :
                <></>
              }

              {(pointHistory.note) ?
                <>
                  <div className="gap-1 pt-5">
                    <div className="text-lg font-bold mb-2">備考</div>
                    <span className="ml-4 whitespace-pre-line">{pointHistory.note}</span>
                  </div>
                </>
              :
                <></>
              }
            </div>
          </div>
        }
      </div>
    </main>
  );
}